import { isEqual, cloneDeep } from 'lodash'

import { PlaytesterV2State } from 'app-redux/playtesterV2/types'
import { PlaytesterCardData } from 'app-redux/playtesterV2/types'
import { GameLog } from 'app-redux/playtesterV2/types'
import { zones, Zone } from 'app-redux/playtesterV2/types'

const ignoredCardMetaFields: (keyof Partial<PlaytesterCardData>)[] = ['notes']

function getUpdatedCardsFromZone(oldZone: PlaytesterCardData[], newZone: PlaytesterCardData[]): PlaytesterCardData[] {
  const updatedCardMetaData: PlaytesterCardData[] = []

  // We don't know how to handle this, this function only checks card meta info for changes.
  // If the zones have cards moving into or out of them, use a different function
  if (oldZone.length !== newZone.length) return []

  // The order of the arrays changed, we're not gonna track that <3
  // May want to at some point, but figuring out how to display it is out of scope (particuarilly when you scry 5)
  if (
    !isEqual(
      oldZone.map(meta => meta.id),
      newZone.map(meta => meta.id),
    )
  )
    return []

  // At this point we can guarentee that our Zones are the same size, and in the same order
  // The only thing that can be changing is the meta info about the card
  for (let i = 0; i < oldZone.length; i++) {
    const oldCard = oldZone[i]
    const newCard = newZone[i]

    // Add the ability to ignore certain singular card changes (eg: Card notes changing)
    const cardMetaKeys = Object.keys(newCard) as (keyof PlaytesterCardData)[]
    const changedFields = cardMetaKeys.reduce((acc, cardMetaKey: keyof PlaytesterCardData) => {
      const oldValue = oldCard[cardMetaKey]
      const newValue = newCard[cardMetaKey]

      if (isEqual(oldValue, newValue)) return acc
      if (ignoredCardMetaFields.includes(cardMetaKey)) return acc

      // They are NOT equal and they are NOT ignored fields
      acc.push(cardMetaKey)

      return acc
    }, new Array<string>())

    if (changedFields.length) updatedCardMetaData.push(newCard)
  }

  return updatedCardMetaData
}

function getCardsFromSecondArgNotInFirst(
  oldZone: PlaytesterCardData[],
  newZone: PlaytesterCardData[],
): PlaytesterCardData[] {
  const cardsFromSecondNotInFirst: PlaytesterCardData[] = []
  const oldZoneIds = oldZone.map(meta => meta.id)

  for (const cardMeta of newZone) {
    if (!oldZoneIds.includes(cardMeta.id)) cardsFromSecondNotInFirst.push(cardMeta)
  }

  return cardsFromSecondNotInFirst
}

export function logCardMoves(oldState: PlaytesterV2State, newState: Partial<PlaytesterV2State>) {
  // Allows the user to hard set the gameLog to whatever they want (eg: removing items from the log list, or emptying out logs on new game)
  if (newState.gameLog) return newState.gameLog

  const keysWithChanges = Object.keys(newState)
  const currentTurn = newState.turnCounter ?? oldState.turnCounter
  const updatedGameLog: GameLog = cloneDeep(oldState.gameLog)

  if (!zones.some(zone => keysWithChanges.includes(zone)) && !keysWithChanges.includes('turnCounter'))
    return updatedGameLog

  // Create empty turn objects for each turn if they do not exist (useful when someone increases their turn count by more than 1 at a time)
  // We are currentTurn + 1 because we want to count turn 0 (for your starting hand)
  // After this point, we don't care about turn counter (beyond editing only the current turn), because we've setup the log array to account for any given turn count
  for (let i = 0; i < currentTurn + 1; i++) {
    if (!Array.isArray(updatedGameLog[i])) updatedGameLog[i] = []
  }

  // @ts-ignore - trust me, it's a zone
  const changedZones: Zone[] = keysWithChanges.filter(key => zones.some(zone => zone === key))

  // If only one zone was changed, then one of two things has happened
  if (changedZones.length === 1) {
    const changedZone = changedZones[0]
    const oldZone = oldState[changedZone]
    const newZone = newState[changedZone]

    if (!newZone || changedZone === 'miscCards') return updatedGameLog

    // If the zones are the same size - a card meta changed (eg: tapped/ untapped)
    const changeIsCardMetaChangeOnly = oldZone.length === newZone.length

    if (changeIsCardMetaChangeOnly) {
      const changedCardMetas = getUpdatedCardsFromZone(oldZone, newZone)

      updatedGameLog[currentTurn].push(
        ...changedCardMetas.map(meta => ({ ...meta, fromZone: null, toZone: null, zone: changedZone })),
      )

      return updatedGameLog
    }

    // Handle case where we added a card from nowhere to somewhere (AKA - adding a token), and from somewhere to nowhere (AKA - hard deleting a card)
    const cardsWereAdded = newZone.length > oldZone.length

    // Cards that are added from nowhere
    if (cardsWereAdded) {
      const addedCardMetas = getCardsFromSecondArgNotInFirst(oldZone, newZone)

      updatedGameLog[currentTurn].push(
        ...addedCardMetas.map(meta => ({ ...meta, fromZone: null, toZone: changedZone, zone: changedZone })),
      )

      return updatedGameLog
    }

    // Cards that are removed,and sent nowhere (hard delete)
    const removedCardMetas = getCardsFromSecondArgNotInFirst(newZone, oldZone)

    updatedGameLog[currentTurn].push(
      ...removedCardMetas.map(meta => ({ ...meta, fromZone: changedZone, toZone: null, zone: 'miscCards' as Zone })),
    )

    return updatedGameLog
  }

  // If we have 2 changed zones some cards move from zone to zone
  // check the 2 zones if one is larger and one smaller (of the changed zones) the larger is the toZone, the smaller the fromZone
  if (changedZones.length === 2) {
    const changedZoneOne = changedZones[0]
    const changedZoneTwo = changedZones[1]

    // This is what happens when searching for arbitrary cards
    if (changedZoneOne === 'miscCards' && changedZoneTwo === 'miscCards') return updatedGameLog

    const oldZoneOne = oldState[changedZoneOne]
    const oldZoneTwo = oldState[changedZoneTwo]

    const newZoneOne = newState[changedZoneOne]
    const newZoneTwo = newState[changedZoneTwo]

    if (!newZoneOne || !newZoneTwo) return updatedGameLog

    const movedFromZoneOneToTwo = oldZoneOne.length > newZoneOne.length && oldZoneTwo.length < newZoneTwo.length

    const fromZone = movedFromZoneOneToTwo ? changedZoneOne : changedZoneTwo
    const toZone = movedFromZoneOneToTwo ? changedZoneTwo : changedZoneOne
    const movedCardMetas = movedFromZoneOneToTwo
      ? getCardsFromSecondArgNotInFirst(oldZoneTwo, newZoneTwo)
      : getCardsFromSecondArgNotInFirst(oldZoneOne, newZoneOne)

    updatedGameLog[currentTurn].push(...movedCardMetas.map(meta => ({ ...meta, fromZone, toZone, zone: toZone })))
  }

  return updatedGameLog
}
